import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setActiveGroup, setCollapsed, setMobileOpen } from '@kss/layouts';
import navData from './sidebarNav.json';
import type { NavItem } from '../../types';
import IconRail from './IconRail';
import SubmenuPanel from './SubmenuPanel';
import { KristyLogo } from '../../common/KristyLogo';
import { cn } from '../../utils/cn';

const staticNavItems = navData as NavItem[];

export interface SidebarProps {
  /**
   * Optional override of the nav tree. When provided AND non-empty,
   * replaces the static `sidebarNav.json` config — this is how the
   * panel app feeds in the dynamic menu fetched from /api/Menu/all
   * + /api/RoleMenu/all. When omitted (or empty), falls back to the
   * static config so the standalone UI showcase still works.
   */
  items?: NavItem[];
}

export default function Sidebar({ items }: SidebarProps = {}) {
  const dispatch = useDispatch();
  const location = useLocation();
  const { collapsed, activeGroupId, mobileOpen } = useSelector((s: any) => s.sidebar);
  // Auto-close the mobile drawer when navigating to a new route so the
  // user doesn't have to manually dismiss it after each tap.
  useEffect(() => {
    if (mobileOpen) dispatch(setMobileOpen(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Pull the current user's role + permission codes from the Redux auth slice
  // so the nav can be filtered without taking a hard dependency on @kss/auth.
  const auth = useSelector((s: any) => s.auth ?? {});
  const role = String(auth?.user?.role ?? '').toLowerCase();
  const permissions: string[] = Array.isArray(auth?.user?.permissions)
    ? auth.user.permissions.map((p: string) => String(p).toLowerCase())
    : [];

  const navItems = useMemo(() => {
    const source = items && items.length > 0 ? items : staticNavItems;
    return filterNavByPermissions(source, role, permissions);
  }, [items, role, permissions.join(',')]);

  // Auto-select nav group based on current route.
  useEffect(() => {
    const match = navItems.find(
      (item) =>
        item.children?.some((child) => location.pathname === child.path) ||
        item.path === location.pathname
    );
    if (match && match.id !== activeGroupId) {
      dispatch(setActiveGroup(match.id));
    }
  }, [location.pathname, navItems]);

  const handleGroupSelect = (id: string): void => {
    const item = navItems.find((n) => n.id === id);
    const hasChildren = !!item?.children?.length;

    if (!hasChildren) {
      // Leaf icon: IconRail handles navigation. Don't mutate the expanded
      // state — the topbar hamburger is the sole collapse control.
      return;
    }

    if (collapsed && hasChildren) {
      // Collapsed rail → expand submenu for this group
      dispatch(setCollapsed(false));
      dispatch(setActiveGroup(id));
      return;
    }

    if (!collapsed && id === activeGroupId && hasChildren) {
      // Same group clicked again while expanded → collapse back to icon rail
      dispatch(setCollapsed(true));
      return;
    }

    dispatch(setActiveGroup(id));
  };

  return (
    <>
      {/* Backdrop — only visible on mobile when the drawer is open. */}
      {mobileOpen && (
        <div
          className="no-print fixed inset-0 z-30 bg-black/40 md:hidden"
          aria-hidden
          onClick={() => dispatch(setMobileOpen(false))}
        />
      )}

      <aside
        className={cn(
          'no-print flex h-full flex-col bg-surface-sidebar',
          // Mobile: fixed-positioned drawer, slides in from the left.
          // Desktop (md+): inline in the flex row, normal behaviour.
          'fixed inset-y-0 left-0 z-40 transform transition-transform duration-200 ease-out md:relative md:transform-none md:transition-none',
          mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
          'flex-shrink-0',
        )}
      >
        {/* Unified Kristy Software logo spanning the sidebar (icon rail
            + submenu). Collapsed → glyph only; expanded → full
            "Kristy SOFTWARE" wordmark, contrast-aware. */}
        <div
          className={cn(
            'flex h-14 shrink-0 items-center gap-2 border-b border-r border-border bg-surface-sidebar px-4',
            collapsed ? 'w-16 justify-center px-0' : 'w-[304px]'
          )}
        >
          {collapsed ? (
            <KristyLogo size={28} variant="glyph" />
          ) : (
            <KristyLogo size={30} variant="full" />
          )}
        </div>

        <div className="flex min-h-0 flex-1">
          <IconRail
            items={navItems}
            activeGroupId={activeGroupId}
            collapsed={collapsed}
            onGroupSelect={handleGroupSelect}
          />
          {!collapsed && (
            <SubmenuPanel items={navItems} activeGroupId={activeGroupId} />
          )}
        </div>
      </aside>
    </>
  );
}

/**
 * Drop nav items the current user can't see.
 *
 * - `roles` and `permissions` on a NavItem/NavChild are *any-match* — if
 *   either intersects the user's role/permission set the item is allowed.
 * - Items with neither `roles` nor `permissions` are public.
 * - Admin / SuperAdmin users see everything regardless.
 * - Empty `children` after filtering AND no own `path` ⇒ drop the parent.
 */
function filterNavByPermissions(
  items: NavItem[],
  role: string,
  permissions: string[],
): NavItem[] {
  const isAdminLike = role === 'admin' || role === 'superadmin';
  const allow = (entry: { roles?: string[]; permissions?: string[] }): boolean => {
    if (isAdminLike) return true;
    const r = entry.roles;
    const p = entry.permissions;
    if (!r?.length && !p?.length) return true;
    if (r?.length && r.some((x) => x.toLowerCase() === role)) return true;
    if (p?.length && p.some((x) => permissions.includes(x.toLowerCase()))) return true;
    return false;
  };

  const out: NavItem[] = [];
  for (const item of items) {
    if (!allow(item)) continue;
    const children = (item.children ?? []).filter(allow);
    // Drop a parent that lost all its children AND has no own path.
    if (item.children?.length && children.length === 0 && !item.path) continue;
    out.push({ ...item, children: item.children ? children : undefined });
  }
  return out;
}
